import { useCallback, useMemo, useState } from 'react';
import { format, sub } from 'date-fns';
import { useActions, useAppSelector } from 'state/store';
import { DATE_DD_MON_YEAR } from 'constants/dateFormat';
import {
  useLazyGetTasksQuery,
  useGetTasksQuery,
} from 'services/newApi/jobsApi';
import { JOB_VALUE_ALL } from '../constants';
import { filterJobsList, sortByStarted } from '../utils';
import { JobItem } from '../types';

export type UseJobsPage = {
  todayList: JobItem[];
  yesterdayList: JobItem[];
  olderList: JobItem[];
  selectedType: string;
  onReportTypeChange: (type: string) => void;
  selectedStatus: string;
  onChangeStatus: (type: string) => void;
  selectedStarted: string;
  datePickerValue: Date;
  onChangeSelectedStarted: (date: Date | Date[]) => void;
  onApplyFilters: () => void;
  onReset: () => void;
  isLoading: boolean;
  isError: boolean;
  onViewReport: () => void;
  onDropdownChange: (option: string) => void;
};

export const useJobsPage = (): UseJobsPage => {
  const todayFormattedDate = format(new Date(), DATE_DD_MON_YEAR);

  const yesterdayFormattedDate = format(
    sub(new Date(), { days: 1 }),
    DATE_DD_MON_YEAR
  );

  const [refetchTasks] = useLazyGetTasksQuery();

  const {
    isSuccess: isTaskSuccess,
    isLoading: isTaskLoading,
    isError: isTaskError,
  } = useGetTasksQuery(undefined, { pollingInterval: 5000 });

  const [datePickerValue, setDatePickerValue] = useState<Date>(new Date());
  const { setJobStatus, setJobType, setJobStarted } = useActions();
  const { selectedStatus, selectedType, jobsList, selectedStarted } =
    useAppSelector((state) => state.jobsState);

  const onReportTypeChange = (type: string) => setJobType(type);
  const onChangeStatus = (status: string) => setJobStatus(status);

  const onChangeSelectedStarted = (date: Date | Date[]) => {
    if (!Array.isArray(date)) {
      setJobStarted(date);
      setDatePickerValue(date);
    }
  };

  const onApplyFilters = () => refetchTasks();

  const onReset = useCallback(() => {
    setJobType(JOB_VALUE_ALL);
    setJobStatus(JOB_VALUE_ALL);
    setJobStarted(undefined);
    setDatePickerValue(new Date());
  }, [setJobStarted, setJobStatus, setJobType]);

  const filteredList = filterJobsList({
    jobsList,
    selectedStarted,
    selectedStatus,
    selectedType,
  }).sort(sortByStarted);

  const todayList: JobItem[] = [];
  const yesterdayList: JobItem[] = [];
  const olderList: JobItem[] = [];

  filteredList.forEach((job) => {
    const jobFormattedDate = format(new Date(job.started), DATE_DD_MON_YEAR);

    if (jobFormattedDate === todayFormattedDate) {
      todayList.push(job);
      return;
    }

    if (jobFormattedDate === yesterdayFormattedDate) {
      yesterdayList.push(job);
      return;
    }

    olderList.push(job);
  });

  const isLoading = useMemo(() => {
    if (isTaskSuccess || isTaskError) {
      return false;
    }
    if (isTaskLoading) {
      return true;
    }
    return false;
  }, [isTaskError, isTaskLoading, isTaskSuccess]);

  const isError = useMemo(() => {
    if (isTaskError) {
      return true;
    }
    return false;
  }, [isTaskError]);

  const onViewReport = useCallback(() => {
    // TODO: Implemente this feature
    // eslint-disable-next-line no-console
    console.log('INFO:: view button');
  }, []);

  const onDropdownChange = useCallback((option: string) => {
    // TODO: Implemente this feature
    // eslint-disable-next-line no-console
    console.log(`INFO:: ${option}`);
  }, []);

  return {
    todayList,
    yesterdayList,
    olderList,
    selectedType,
    onReportTypeChange,
    selectedStatus,
    onChangeStatus,
    selectedStarted,
    datePickerValue,
    onChangeSelectedStarted,
    onApplyFilters,
    onReset,
    isLoading,
    isError,
    onViewReport,
    onDropdownChange,
  };
};
